// apps/web/src/App.tsx

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { QueryClientProvider } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { RouterProvider } from 'react-router-dom';

import { PricingPermissionsProvider, queryClient } from '@alamo/api';
import { AuthProvider, usePermissions } from '@alamo/auth';
import { ThemeProvider, TooltipProvider } from '@alamo/ui';

import { EnvBanner } from './components/EnvBanner';
import { PwaUpdatePrompt } from './components/PwaUpdatePrompt';
import { isPublicNoPwaRoute } from './lib/pwaScope';
import { router } from './routes';

// E2E harness stale-state probe: expose the QueryClient on window in dev/test so a
// Playwright run can read TanStack cache freshness (which query refetched after a
// mutation). Dev-only — never reaches a production build.
if (import.meta.env.DEV) {
  (window as unknown as { __ALAMO_QC__?: unknown }).__ALAMO_QC__ = queryClient;
}

function PricingPermissionsBridge({ children }: { children: ReactNode }) {
  const { hasPermission } = usePermissions();
  return (
    <PricingPermissionsProvider hasPermission={hasPermission}>
      {children}
    </PricingPermissionsProvider>
  );
}

export function App() {
  // Opção B2 — decisão de boot: nos fluxos públicos de uso único não montamos o
  // PwaUpdatePrompt (não registra service worker). Congelada no primeiro render:
  // essas rotas são terminais e login-less, então o consultor não navega delas
  // para o app na mesma sessão. Em rotas com PWA (login + app) o prompt monta.
  const [pwaEnabled] = useState(
    () => typeof window === 'undefined' || !isPublicNoPwaRoute(window.location.pathname),
  );

  useEffect(() => {
    function handlePermissionDenied(event: Event) {
      const detail = (event as CustomEvent<{ message: string; permission?: string }>).detail;
      toast.error(detail.message, {
        duration: 4000,
        id: `perm-denied-${detail.permission ?? detail.message}`,
      });
    }

    window.addEventListener('alamo:permission-denied', handlePermissionDenied);
    return () => window.removeEventListener('alamo:permission-denied', handlePermissionDenied);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <PricingPermissionsBridge>
            <TooltipProvider>
              <div className="flex h-screen flex-col">
                {pwaEnabled && <PwaUpdatePrompt />}
                <EnvBanner />
                {/* v7_startTransition: navegações via React.startTransition (default do v7).
                    v7_relativeSplatPath NÃO habilitada: InspectionFlowPage usa <Routes>
                    descendentes com <Navigate to="intro"> relativo sob a rota splat
                    /p/:token/inspection/* — a flag muda exatamente essa resolução. */}
                <div className="min-h-0 flex-1">
                  <RouterProvider router={router} future={{ v7_startTransition: true }} />
                </div>
              </div>
            </TooltipProvider>
          </PricingPermissionsBridge>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
